// src\features\Cart\components\Base.tsx
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import { useTransactionStore } from "../store/transactionStore";
import { useState } from "react";
import type { CartItem } from "@/types/cart.type";
import CartItemList from "./CartItemList";
import CartSummary from "./CartSummary";
import CancelModal from "./CancelModal";
import PendingModal from "./PendingModal";
import { useCreateTransaction } from "@/features/Payment/hooks/useCreateTransaction";
import toast from "react-hot-toast";
import { getStoredTransaction } from "@/utils/getStoredUser";
import { formatOutDate } from "@/utils/formatOutDate";

export default function CartBase() {
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);

  const [cart, updateQty, removeFromCart, clearTransaction, updateDiscount] =
    useTransactionStore(
      useShallow((state) => [
        state.cart,
        state.updateQty,
        state.removeFromCart,
        state.clearTransaction,
        state.updateDiscount,
      ]),
    );

  const typedCart = cart as unknown as CartItem[];

  const increaseQty = (item: CartItem) => {
    if (item.cek_stock && item.cek_stock == 1) {
      if (item.qty < Number(item.stock)) {
        updateQty(item.mtrl_code, item.qty + 1);
      } else {
        toast.error("Stok sudah melebihi batas");
      }
    } else {
      updateQty(item.mtrl_code, item.qty + 1);
    }
  };

  const decreaseQty = (item: CartItem) => {
    if (item.qty > 1) {
      updateQty(item.mtrl_code, item.qty - 1);
    } else {
      removeFromCart(item.mtrl_code);
    }
  };

  const handleCancelTransaction = () => {
    clearTransaction();
    setShowCancelModal(false);
    navigate("/catalog", { replace: true });
  };

  const { mutate: createTransaction, isPending } = useCreateTransaction({
    onSuccess: (data) => {
      clearTransaction();
      setTimeout(() => {
        navigate(`/catalog`, { replace: true });
      }, 1000);
    },
    onError: (error) => {
      toast.error("Transaksi gagal disimpan: " + error.message);
    },
  });

  const handlePendingTransaction = async () => {
    setShowPendingModal(false);
    const data = getStoredTransaction();

    const paymentChannelMap: Record<string, number> = {
      Cash: 1,
      Transfer: 2,
      QRIS: 3,
    };

    const payload = {
      out_no: data.out_no,
      out_date: formatOutDate(data.tanggalTransaksi),
      cust_id: Number(data.customer),
      totalBayar: data.paidAmount,
      channelId: paymentChannelMap[data.metodePembayaran] || 0,
      payment_status: data.paidAmount > 0 ? 1 : 0,
      is_pending: 1,
      diskon_total: data.diskonSubtotal, // rupiah
      ppn: data.ppn, // rupiah

      items:
        data?.cart?.map((item: any) => ({
          mtrl_code: item.mtrl_code,
          qty: item.qty,
          diskon: item.discount || 0, // rupiah
        })) || [],
    };

    await createTransaction(payload);
  };

  if (typedCart.length === 0) {
    return (
      <div className=" lg:w-[350px] xl:w-[400px] bg-[#fefeff] dark:bg-transparent flex items-center justify-center pt-25 ">
        <div className="relative text-center ">
          <div className="mb-4 text-6xl">🛒</div>
          <p className="text-lg text-gray-500 dark:text-white">
            Keranjang kosong
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className=" bg-[#fefeff] dark:bg-[#37393d] pb-2 relative lg:w-[350px] xl:w-[450px] ">
      <CartItemList
        cart={typedCart}
        onIncrease={increaseQty}
        onDecrease={decreaseQty}
        onUpdateDiscount={updateDiscount}
      />

      <div className="relative lg:fixed lg:bottom-[72px] lg:right-6 lg:w-[350px] xl:w-[450px] ">
        <CartSummary
          onCancel={() => setShowCancelModal(true)}
          onPending={() => setShowPendingModal(true)}
        />
      </div>

      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelTransaction}
      />
      <PendingModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
        onConfirm={handlePendingTransaction}
      />
    </div>
  );
}
